import React, { useEffect } from 'react';
import styled from 'styled-components';

import { initializeFixedTable } from './helper';

const scrollStyle = `
  &[data-scroll-left='0'] .scroll-control.previous {
    opacity: 0 !important;
  }

  &[data-records='0'] {
    .scroll-control {
      display: none !important;
    }
    .uds-table > table tbody tr:hover td {
      background-color: initial;
    }
  }

  &:hover .scroll-control {
    visibility: visible;
    opacity: 1;
  }

  & .scroll-control {
    display: block;
    position: absolute;
    height: 100%;
    pointer-events: none;
    top: 0;
    z-index: 100;
    visibility: hidden;
    opacity: 0;
    transition: visibility 0.3s linear, opacity 0.3s linear;
  }

  div[data-loading="true"] & .scroll-control{
    opacity: 0 !important;
  }
`;

const Container = styled.div.attrs(({ columFixedLayout }) => ({
  columFixedLayout,
}))`
  ${(props) => (props.columFixedLayout ? scrollStyle : '')}

  .uds-table:focus {
    box-shadow: none !important;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody {
    overflow-y: scroll;
  }

  th {
    white-space: nowrap;
    text-transform: capitalize;
  }

  th[data-sortable='true'] {
    cursor: pointer;
  }

  td {
    vertical-align: baseline;
  }

  /* CSS-only: when capped rows are enabled, the outer wrapper scrolls */
  .uds-table[data-max-rows='true'] {
    /* Defaults can be tuned via theme or overridden inline */
    --thead-h: 52px;
    --row-h: 54px;
    /* Use --cb-max-rows to set the number of visible rows */
    max-height: calc(var(--thead-h) + var(--row-h) * var(--cb-max-rows, 10));
    overflow-y: auto;
  }

  /* Avoid nested scrollbars; let tbody expand inside the capped wrapper */
  .uds-table[data-max-rows='true'] tbody {
    overflow-y: visible;
  }
`;

/**
 * @param {PropsWithChildren<{
 *  height?: string,
 *  recordCount: number,
 *  columFixedLayout: boolean,
 *  maxVisibleRows?: number,
 * }>} props
 */
const TableContainer = ({
  recordCount,
  height,
  columFixedLayout = false,
  maxVisibleRows = undefined,
  children,
}) => {
  useEffect(() => {
    columFixedLayout && initializeFixedTable();
  }, [columFixedLayout, recordCount]);

  // CSS-only cap: mark wrapper as capped only when rows exceed the max.
  const capped = !!maxVisibleRows && recordCount > maxVisibleRows;

  return (
    <Container
      className={columFixedLayout ? 'uds-table-fixed-wrapper' : ''}
      columFixedLayout={columFixedLayout}
      data-records={recordCount}
      data-scroll-left="0"
    >
      {columFixedLayout && (
        <>
          <div className="scroll-control previous">
            <button
              type="button"
              className="btn btn-circle btn-circle-alt-gray"
            >
              <i className="fas fa-chevron-left" />
              <span className="sr-only">Previous</span>
            </button>
          </div>

          <div className="scroll-control next">
            <button
              type="button"
              className="btn btn-circle btn-circle-alt-gray"
            >
              <i className="fas fa-chevron-right" />
              <span className="sr-only">Next</span>
            </button>
          </div>
        </>
      )}

      <div
        className={columFixedLayout ? 'uds-table uds-table-fixed' : 'uds-table'}
        tabIndex={0}
        style={{
          height,
          ...(maxVisibleRows
            ? {
                '--cb-max-rows': maxVisibleRows,
                // Tune these to approximate 10 visible rows with the table's current padding/typography.
                '--thead-h': '52px',
                '--row-h': '58px',
              }
            : {}),
        }}
        data-max-rows={capped ? 'true' : undefined}
      >
        {children}
      </div>
    </Container>
  );
};

export { TableContainer };
